import { Listbox } from '@headlessui/react'
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '../icons'
import { FieldPath, FieldValues, useController } from 'react-hook-form'
import './Select.css'
import { defaultGetOptionDisplay, defaultGetValueKey, defaultToValue, usePopperSelectDropdown } from '../utils'
import { ControlledInputProps, FieldBindingsProps, OptionsProps } from '../../types'
import clsx from 'clsx'
import ButtonGroup from '../ButtonGroup'

export interface SingleSelectProps<T, O = T> extends ControlledInputProps<T>, OptionsProps<O, T> {
    className?: string
    buttonClassName?: string
    placeholder?: ReactNode
    getSelectedOptionDisplay?: (option: O) => ReactNode
    getOptionSubtitle?: (option: O) => ReactNode
    onBlur?: () => void
    optionsFooter?: ReactNode
    size?: 'small' | 'regular'
    variant?: 'plain' | 'minimal'
    prefix?: ReactNode
    suffix?: ReactNode
    searchable?: boolean
    searchPlaceholder?: string
}

export function SingleSelect<T, U = T>({
    buttonClassName,
    className,
    disabled,
    placeholder,
    error,
    getOptionDisplay = defaultGetOptionDisplay,
    getSelectedOptionDisplay = getOptionDisplay,
    getOptionSubtitle,
    hideLabel,
    label,
    options,
    optionsFooter,
    onBlur,
    onChange,
    required,
    size,
    subtitle,
    toValue = defaultToValue,
    getValueKey = defaultGetValueKey,
    value,
    variant,
    prefix,
    suffix,
    searchable,
    searchPlaceholder,
}: SingleSelectProps<T, U>) {

    const {
        setReferenceElement,
        setPopperElement,
        attributes,
        styles,
    } = usePopperSelectDropdown()

    const [search, setSearch] = useState('')
    const searchRef = useRef<HTMLInputElement>(null)

    const setOptionsRef = useCallback((el: HTMLElement | null) => {
        setPopperElement(el)
        if (el && searchable) {
            setSearch('')
            requestAnimationFrame(() => searchRef.current?.focus())
        }
    }, [searchable, setPopperElement])

    const selectedOption = options.find(o => Object.is(getValueKey(toValue(o)), getValueKey(value)))

    const filteredOptions = useMemo(() => {
        if (!searchable || !search) return options
        const q = search.toLowerCase()
        return options.filter(o => {
            const display = String(getOptionDisplay(o)).toLowerCase()
            const sub = getOptionSubtitle ? String(getOptionSubtitle(o)).toLowerCase() : ''
            return display.includes(q) || sub.includes(q)
        })
    }, [options, search, searchable, getOptionDisplay, getOptionSubtitle])

    const showPrefix = !!prefix || !!(placeholder && !label && value)

    return (
        <Listbox
            as="div"
            className={clsx(
                'ui-select',
                className,
                variant ?? 'plain',
                showPrefix && 'ui-select-prefix-wrapper',
            )}
            by={(left: T, right: T) => Object.is(getValueKey(left), getValueKey(right))}
            disabled={disabled}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
        >
            {
                label && (
                    <Listbox.Label style={hideLabel ? { display: 'none' } : undefined}>
                        <span>
                            {label}
                            {
                                required && (
                                    <span style={{ color: 'red' }}>&nbsp;*</span>
                                )
                            }
                        </span>
                    </Listbox.Label>
                )
            }
            {
                subtitle && (
                    <span className="label-subtitle">
                        {subtitle}
                    </span>
                )
            }
            <ButtonGroup>
                {
                    showPrefix && (
                        <span className={clsx('ui-select-prefix', size)}>
                            {prefix ?? placeholder}
                        </span>
                    )
                }
                <Listbox.Button className={clsx('select-button', size, buttonClassName)} ref={setReferenceElement}>
                    <span className="select-button-label">
                        {
                            selectedOption === undefined
                                ? placeholder ?? ''
                                : getSelectedOptionDisplay(selectedOption)
                        }
                    </span>
                    <span className="select-button-icon">
                        <ChevronUpDownIcon aria-hidden="true" />
                    </span>
                </Listbox.Button>
                {suffix}
            </ButtonGroup>
            {
                (error && !hideLabel) && (
                    <span className="field-error">
                        {error}
                    </span>
                )
            }
            <Listbox.Options
                className="select-options nowheel"
                ref={setOptionsRef}
                style={styles.popper}
                {...attributes.popper}
            >
                {searchable && (
                    <li className="select-search">
                        <input
                            ref={searchRef}
                            type="text"
                            className="select-search-input"
                            placeholder={searchPlaceholder ?? 'Search\u2026'}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => e.stopPropagation()}
                        />
                    </li>
                )}
                {filteredOptions.map((option) => {
                    const value = toValue(option)
                    return (
                        <Listbox.Option
                            key={getValueKey(value)}
                            value={value}
                            className={({ focus, selected }) => clsx(
                                'select-option',
                                focus && 'active',
                                selected && 'selected',
                            )}
                        >
                            <span className={getOptionSubtitle ? 'select-option-content' : undefined}>
                                <span className="select-option-title">{getOptionDisplay(option)}</span>
                                {getOptionSubtitle && (
                                    <span className="select-option-subtitle">{getOptionSubtitle(option)}</span>
                                )}
                            </span>
                            <span className="option-icon">
                                <CheckIcon aria-hidden="true" />
                            </span>
                        </Listbox.Option>
                    )
                })}
                {filteredOptions.length === 0 && (
                    <li className="select-option-empty">No results</li>
                )}
                {optionsFooter}
            </Listbox.Options>
        </Listbox>
    )
}

SingleSelect.Field = function SingleSelectField<T, O, X extends FieldValues, P extends FieldPath<X>>({
    form,
    name,
    required,
    ...rest
}: FieldBindingsProps<SingleSelectProps<T, O>, T, X, P>) {

    const { field: { ref, ...field }, fieldState } = useController({
        control: form.control,
        name,
        rules: {
            required,
        },
    })

    return (
        <SingleSelect
            {...rest}
            {...field}
            required={required}
            error={fieldState.error?.message}
        />
    )
}
